import { marked } from "marked";
import { ApiService } from "../shared/utils/ApiService.js";
import { askWithStream, collectChatHistory } from "../shared/utils/ChatStreamHelper.js";
import { TemplateEngine } from "../shared/utils/TemplateEngine.js";
import { Navbar } from "../shared/components/navbar/Navbar.js";
import { AuthService } from "../shared/services/AuthService.js";
import { AppConfig } from "../shared/utils/AppConfig.js";

import knowledgeTemplate from "./templates/knowledge.html?raw";
import articleTemplate from "./templates/article.html?raw";
import apiConnectorTemplate from "./templates/api-connector.html?raw";
import Swal from "sweetalert2";
import { HistorialService } from "../shared/utils/HistorialService.js";
import { TourService } from "../shared/utils/TourService.js";
import { escapeHtml, sanitizeHtml } from "../shared/utils/sanitize.js";
import { isImageFile, iconForFile, formatSize, previewKind } from "../shared/utils/adjuntos.js";
import { relacionadosDe } from "../shared/utils/relacionados.js";
import {
  subcategoriasConConteo, nombreSubcategoria, subcategoriaDeArticulo, rutaArticuloHtml,
} from "../shared/utils/subcategorias.js";

const AI_SEARCH_KEY = "kb_ai_search_active";
const VIEW_PREF_KEY = "kb_knowledge_view";
function getAiSearchActive() { return sessionStorage.getItem(AI_SEARCH_KEY) === "true"; }
function setAiSearchActive(val) { sessionStorage.setItem(AI_SEARCH_KEY, val ? "true" : "false"); }
function getSavedView() { return localStorage.getItem(VIEW_PREF_KEY) || "list"; }
function saveView(view) { localStorage.setItem(VIEW_PREF_KEY, view); }

function getCategoriasPermitidas(user) {
  if (!user) return null;
  if (user.rol === "admin") return null;
  return Array.isArray(user.categorias) && user.categorias.length > 0 ? user.categorias : null;
}

const TIPO_ICONOS = { texto: "fa-file-lines", pdf: "fa-file-pdf", video: "fa-circle-play", imagen: "fa-image", audio: "fa-headphones" };
const TIPO_LABELS = { texto: "Texto", pdf: "PDF", video: "Video", imagen: "Imagen", audio: "Audio" };

function renderMarkdown(text) {
  if (!text) return "";
  return marked.parse(text, { breaks: true });
}

export const KnowledgeController = {

  render: async(params = new URLSearchParams()) => {
    try {
      const catFilter = params.get("cat") || null;
      // Subcategoría (id o "none"): solo con flag y dentro de una categoría
      const subFilter = AppConfig.features.subcategories && catFilter ? (params.get("sub") || null) : null;
      const tagFilter = params.get("tag") || null;
      const searchQuery = params.get("q") || "";
      const currentPage = Math.max(1, parseInt(params.get("page")) || 1);
      const PAGE_SIZE = 20;

      const user = AuthService.getCurrentUser();
      const categoriasPermitidas = getCategoriasPermitidas(user);

      const basePayload = categoriasPermitidas ? { categoriasPermitidas } : {};
      const aiSearchEnabled = AppConfig.features.aiSearch;
      const aiSearchActive = aiSearchEnabled && !!searchQuery && getAiSearchActive();

      // Request principal (paginada) + contadores del sidebar (sin page) en paralelo
      const [articulosResp, categoriasResp, countsResp] = await Promise.all([
        ApiService.post("kb/articulos", {
          ...basePayload,
          ...(catFilter ? { categoriaId: catFilter } : {}),
          ...(subFilter ? { subcategoriaId: subFilter } : {}),
          ...(tagFilter ? { tag: tagFilter } : {}),
          ...(searchQuery ? { q: searchQuery } : {}),
          ...(aiSearchActive ? { ai: true } : {}),
          page: currentPage,
          pageSize: PAGE_SIZE,
        }),
        ApiService.get("categorias"),
        ApiService.post("kb/articulos", basePayload), // sin page → todos, para contadores
      ]);

      const allArticulos = articulosResp?.data || [];
      const totalPages = articulosResp?.totalPages ?? 1;
      const totalResults = articulosResp?.total ?? allArticulos.length;
      const categorias = categoriasResp?.data || [];
      const allForCounts = countsResp?.data || [];

      const catMap = Object.fromEntries(categorias.map(c => [c.id, c]));
      const apiEnabled = AppConfig.features.apiConnector;

      // Filtrar categorías api si el feature flag está deshabilitado
      const categoriasVisibles = categorias.filter(c =>
        // Las públicas se ven aunque no estén entre las permitidas (igual que en el Worker)
        (!categoriasPermitidas || categoriasPermitidas.includes(c.id) || !!c.es_publica) &&
        (apiEnabled || c.tipo !== 'api')
      );

      // Si la categoría activa es de tipo api y el feature está habilitado → vista conector
      const catActivaData = catFilter ? catMap[catFilter] : null;
      if (apiEnabled && catActivaData?.tipo === 'api') {
        await KnowledgeController._renderApiConnector(catActivaData, categoriasVisibles, catFilter);
        return;
      }

      const categoriasWithMeta = categoriasVisibles.map(c => {
        const activa = c.id === catFilter;
        const subs = AppConfig.features.subcategories ? subcategoriasConConteo(c, allForCounts) : [];
        return {
          ...c,
          count: allForCounts.filter(a => a.categoriaId === c.id).length,
          // Sin la clase "active" en la categoría cuando el filtro está en una subcategoría
          activa: activa && !subFilter,
          chevronHtml: subs.length
            ? `<i class="fas fa-chevron-${activa ? "down" : "right"} kb-cat-chevron"></i>`
            : "",
          // La categoría activa se despliega con sus subcategorías (HTML pre-renderizado: sin {{#each}} anidado)
          subsHtml: activa ? KnowledgeController._buildSubcatsHtml(c.id, subs, subFilter, false) : "",
          subsHtmlMobile: activa ? KnowledgeController._buildSubcatsHtml(c.id, subs, subFilter, true) : "",
        };
      });
      const subNombre = nombreSubcategoria(catActivaData, subFilter);

      const articulos = allArticulos.map(a => ({
        ...a,
        tipoIcono: TIPO_ICONOS[a.tipo] || "fa-file",
        tipoLabel: TIPO_LABELS[a.tipo] || a.tipo,
        rutaHtml: rutaArticuloHtml(
          catMap[a.categoriaId]?.nombre,
          AppConfig.features.subcategories ? subcategoriaDeArticulo(catMap[a.categoriaId], a) : ""
        ),
        tagsHtml: (a.tags || []).map(t =>
          `<a href="#knowledge?tag=${encodeURIComponent(t)}" class="badge bg-secondary text-decoration-none me-1 kb-tag-badge">${t}</a>`
        ).join(""),
        hasTags: !!(a.tags?.length),
      }));

      const data = {
        categorias: categoriasWithMeta,
        articulos,
        totalArticulos: allForCounts.length,
        totalResultados: totalResults,
        noFilter: !catFilter && !searchQuery,
        hasCatActiva: !!catActivaData,
        catActiva: catActivaData ? catActivaData.nombre + (subNombre ? ` › ${escapeHtml(subNombre)}` : "") : "",
        catColorActiva: catActivaData?.color || "primary",
        catIconoActiva: catActivaData?.icono || "",
        catDescActiva: catActivaData?.descripcion || "",
        searchQuery,
        tagFilter,
        noResults: articulos.length === 0,
        aiRestricto: AppConfig.features.aiRestrictToCategories && !!categoriasPermitidas,
        aiScopeLabel: catActivaData?.nombre || (categoriasPermitidas ? "tus categorías" : "toda la base"),
        aiSearchToggleHtml: aiSearchEnabled
          ? `<button type="button" id="kb-ai-toggle"
               class="btn btn-sm px-3 ${aiSearchActive ? "btn-warning" : "btn-outline-secondary"}"
               title="${aiSearchActive ? "Búsqueda semántica IA activa — clic para desactivar" : "Activar búsqueda semántica IA"}">
               <i class="fas fa-${aiSearchActive ? "star" : "wand-magic-sparkles"}"></i>
             </button>`
          : "",
        aiSearchBadgeHtml: aiSearchActive
          ? `<div class="px-1 pt-1">
               <span class="badge text-bg-warning small">
                 <i class="fas fa-star me-1"></i>Búsqueda semántica IA activa
               </span>
             </div>`
          : "",
        paginacionHtml: KnowledgeController._buildPaginationHtml(currentPage, totalPages, catFilter, searchQuery, subFilter),
      };

      const content = TemplateEngine.render(knowledgeTemplate, data);
      document.getElementById("app").innerHTML = await Navbar.render() + content;

      KnowledgeController._bindSearch(catFilter);
      KnowledgeController._bindFiltersCanvasNav();
      KnowledgeController._bindAiToggle(searchQuery, catFilter);
      KnowledgeController._bindViewToggle();
      KnowledgeController._bindTypeFilter();
      // Con una categoría elegida, el chat busca solo dentro de ella
      KnowledgeController._bindInlineChat(catFilter);
      KnowledgeController._bindInlineChatMobile(catFilter);
      KnowledgeController._bindSuggestBtn(searchQuery);
      KnowledgeController._bindSuggestBtn(searchQuery, "kb-suggest-btn-mobile");
      TourService.resumeIfPending();
    } catch (error) {
      console.error("Error loading knowledge:", error);
      document.getElementById("app").innerHTML =
        await Navbar.render() +
        `<div class="container mt-4"><div class="alert alert-danger">${error.message}</div></div>`;
    }
  },

  renderArticle: async(params = new URLSearchParams()) => {
    try {
      const id = params.get("id");
      if (!id) { window.location.hash = "knowledge"; return; }

      const [artResp, categoriasResp, allResp] = await Promise.all([
        ApiService.post("kb/articulo", { id }),
        ApiService.get("categorias"),
        ApiService.post("kb/articulos", {}),
      ]);

      const art = artResp?.data;
      const cats = categoriasResp?.data || [];
      const allArts = allResp?.data || [];

      if (!art) { window.location.hash = "knowledge"; return; }

      const catMap = Object.fromEntries(cats.map(c => [c.id, c]));
      const catNombre = catMap[art.categoriaId]?.nombre || "";
      const subNombreArt = AppConfig.features.subcategories
        ? subcategoriaDeArticulo(catMap[art.categoriaId], art)
        : "";

      // Relacionados en los dos sentidos: los propios más los artículos que lo tienen en su lista.
      // Fallback: misma categoría.
      const artMap = Object.fromEntries(allArts.map(a => [a.id, a]));
      const relacionados = relacionadosDe(art, allArts).map(id => artMap[id]).filter(Boolean);
      const related = (
        relacionados.length
          ? relacionados
          : allArts.filter(a => a.categoriaId === art.categoriaId && a.id !== art.id).slice(0, 4)
      ).map(a => ({ ...a, tipoIcono: TIPO_ICONOS[a.tipo] || "fa-file" }));

      const extractYtId = (url) => {
        if (!url) return null;
        if (/^[a-zA-Z0-9_-]{7,12}$/.test(url.trim())) return url.trim();
        try {
          const u = new URL(url);
          if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split("?")[0];
          return u.searchParams.get("v");
        } catch { return null; }
      };
      const youtubeId = art.tipo === "video" ? extractYtId(art.urlRecurso) : null;
      const isVideoYt = !!youtubeId;
      const isVideoFile = art.tipo === "video" && !!art.urlRecurso && !youtubeId;

      // Pre-renderizar HTML de transcripción (evita {{#if}} anidado en el template)
      const buildTranscripcionHtml = (texto) => {
        if (!texto) return "";
        const parrafos = texto.split("\n").filter(p => p.trim()).map(p => `<p>${escapeHtml(p)}</p>`).join("");
        return `<div class="border-top pt-3 mt-3">
          <h6 class="text-muted mb-2"><i class="fas fa-file-lines me-1"></i>Transcripción</h6>
          <div class="kb-content-text">${parrafos}</div>
        </div>`;
      };
      const transcripcionHtml = (art.tipo === "audio" || isVideoFile) ? buildTranscripcionHtml(art.transcripcion) : "";

      // Archivos adicionales (el principal ya se muestra en el visor de arriba)
      const archivos = (art.adjuntos || []).filter(a => !a.esPrincipal);
      const archivosHtml = KnowledgeController._buildArchivosHtml(archivos);

      const relatedWithMeta = related.map(a => ({
        ...a,
        descripcionCorta: a.descripcion
          ? a.descripcion.substring(0, 65) + (a.descripcion.length > 65 ? "…" : "")
          : "",
      }));

      const data = {
        ...art,
        tipoIcono: TIPO_ICONOS[art.tipo] || "fa-file",
        tipoLabel: TIPO_LABELS[art.tipo] || art.tipo,
        // TemplateEngine trata 0 como vacío: los contadores van como texto
        vistas: String(art.vistas ?? 0),
        positivos: String(art.positivos ?? 0),
        negativos: String(art.negativos ?? 0),
        categoriaNombre: catNombre,
        subcatCrumbHtml: subNombreArt
          ? `<li class="breadcrumb-item"><a href="#knowledge?cat=${encodeURIComponent(art.categoriaId)}&sub=${encodeURIComponent(art.subcategoriaId)}">${escapeHtml(subNombreArt)}</a></li>`
          : "",
        rutaHtml: rutaArticuloHtml(catNombre, subNombreArt),
        isTexto: art.tipo === "texto",
        isPdf: art.tipo === "pdf",
        isAudio: art.tipo === "audio",
        isVideoYt,
        isVideoFile,
        youtubeId,
        isImagen: art.tipo === "imagen",
        transcripcionHtml,
        archivosHtml,
        hasArchivos: archivos.length > 0,
        related: relatedWithMeta,
        hasRelated: relatedWithMeta.length > 0,
        tagsHtml: (art.tags || []).map(t =>
          `<a href="#knowledge?tag=${encodeURIComponent(t)}" class="badge bg-secondary text-decoration-none me-1 kb-tag-badge">${t}</a>`
        ).join(""),
        hasTags: !!(art.tags?.length),
      };

      const content = TemplateEngine.render(articleTemplate, data);
      document.getElementById("app").innerHTML = await Navbar.render() + content;

      // Render contenido del artículo (solo tipo texto).
      // Quill produce HTML (empieza con "<"); el mock usa markdown.
      if (art.tipo === "texto" && art.contenido) {
        const el = document.getElementById("kb-article-content");
        if (el) {
          const isHtml = art.contenido.trimStart().startsWith("<");
          el.innerHTML = isHtml ? art.contenido : renderMarkdown(art.contenido);
        }
      }

      HistorialService.add(art);

      KnowledgeController._bindArchivos(archivos);
      KnowledgeController._bindVotes(art.id);
      KnowledgeController._bindShare();
      KnowledgeController._bindArticleChat(art.titulo);
      KnowledgeController._bindRelatedToggle();
    } catch (error) {
      console.error("Error loading article:", error);
      document.getElementById("app").innerHTML =
        await Navbar.render() +
        `<div class="container mt-4"><div class="alert alert-danger">${error.message}</div></div>`;
    }
  },

  // Sección "Archivos": imágenes en grilla (visor con navegación) y el resto en lista
  _buildArchivosHtml: (archivos) => {
    if (!archivos.length) return "";
    const imagenes = archivos.filter(a => isImageFile(a.nombre));
    const otros = archivos.filter(a => !isImageFile(a.nombre));

    const grid = imagenes.length
      ? `
      <div class="kb-archivos-grid mb-3">
        ${imagenes.map((a, i) => `
          <button type="button" class="kb-archivo-thumb" data-img-index="${i}" title="${escapeHtml(a.nombre)}">
            <img src="${escapeHtml(a.url)}" alt="${escapeHtml(a.nombre)}" loading="lazy">
          </button>`).join("")}
      </div>`
      : "";

    const lista = otros.length
      ? `
      <div class="list-group">
        ${otros.map(a => {
          const preview = previewKind(a.nombre);
          return `
          <div class="list-group-item d-flex align-items-center gap-2">
            <i class="fas ${iconForFile(a.nombre)} text-muted fa-lg"></i>
            <div class="flex-grow-1 min-w-0">
              <div class="small fw-semibold text-truncate">${escapeHtml(a.nombre)}</div>
              <small class="text-muted">${formatSize(a.tamano)}</small>
            </div>
            ${preview ? `<button type="button" class="btn btn-outline-secondary btn-sm" data-preview-id="${escapeHtml(a.id)}" title="Vista previa"><i class="fas fa-eye"></i></button>` : ""}
            <a href="${escapeHtml(a.url)}" target="_blank" rel="noopener" download class="btn btn-outline-primary btn-sm" title="Descargar"><i class="fas fa-download"></i></a>
          </div>`;
        }).join("")}
      </div>`
      : "";

    return `<div class="border-top pt-3 mt-3" id="kb-archivos">
      <h6 class="text-muted mb-3"><i class="fas fa-paperclip me-1"></i>Archivos (${archivos.length})</h6>
      ${grid}${lista}
    </div>`;
  },

  _bindArchivos: (archivos) => {
    const cont = document.getElementById("kb-archivos");
    if (!cont) return;
    const imagenes = archivos.filter(a => isImageFile(a.nombre));

    // Visor de imágenes: Siguiente / Anterior recorren la grilla
    const verImagen = async(start) => {
      let i = start;
      while (true) {
        const img = imagenes[i];
        const varias = imagenes.length > 1;
        const res = await Swal.fire({
          titleText: `${img.nombre}${varias ? ` (${i + 1}/${imagenes.length})` : ""}`,
          imageUrl: img.url,
          imageAlt: img.nombre,
          width: 900,
          showConfirmButton: varias,
          showDenyButton: varias,
          showCancelButton: true,
          confirmButtonText: 'Siguiente <i class="fas fa-chevron-right ms-1"></i>',
          denyButtonText: '<i class="fas fa-chevron-left me-1"></i>Anterior',
          cancelButtonText: "Cerrar",
          reverseButtons: true,
        });
        if (res.isConfirmed) i = (i + 1) % imagenes.length;
        else if (res.isDenied) i = (i - 1 + imagenes.length) % imagenes.length;
        else return;
      }
    };

    const verArchivo = async(adj) => {
      const kind = previewKind(adj.nombre);
      if (kind === "pdf") {
        Swal.fire({
          titleText: adj.nombre,
          width: "90%",
          showCloseButton: true,
          showConfirmButton: false,
          html: `<iframe src="${escapeHtml(adj.url)}" style="width:100%;height:75vh;border:0" title="${escapeHtml(adj.nombre)}"></iframe>`,
        });
        return;
      }
      try {
        const res = await fetch(adj.url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        const body = kind === "md"
          ? `<div class="kb-content-text">${sanitizeHtml(marked.parse(text))}</div>`
          : `<pre class="mb-0" style="white-space:pre-wrap">${escapeHtml(text)}</pre>`;
        Swal.fire({
          titleText: adj.nombre,
          width: 800,
          showCloseButton: true,
          showConfirmButton: false,
          html: `<div class="text-start" style="max-height:70vh;overflow-y:auto">${body}</div>`,
        });
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo abrir el archivo", text: err.message });
      }
    };

    cont.addEventListener("click", (e) => {
      const thumb = e.target.closest("[data-img-index]");
      if (thumb) return verImagen(Number(thumb.dataset.imgIndex));
      const prev = e.target.closest("[data-preview-id]");
      const adj = prev && archivos.find(a => a.id === prev.dataset.previewId);
      if (adj) verArchivo(adj);
    });
  },

  // Subcategorías de la categoría activa en el sidebar (la versión mobile se cierra en _bindFiltersCanvasNav)
  _buildSubcatsHtml: (catId, subs, subFilter, mobile) => {
    if (!subs.length) return "";
    const dismiss = mobile ? " data-canvas-nav" : "";
    return `<div class="kb-subcat-nav">${subs.map(s => `
      <a href="#knowledge?cat=${encodeURIComponent(catId)}&sub=${encodeURIComponent(s.id)}"
         class="kb-subcat-nav-item ${s.id === subFilter ? "active" : ""}"${dismiss}>
        <span class="flex-grow-1 text-truncate">${escapeHtml(s.nombre)}</span>
        <span class="kb-cat-count">${s.count}</span>
      </a>`).join("")}</div>`;
  },

  // Mobile: los links de categoría/subcategoría del offcanvas cierran el panel y navegan.
  // No se usa data-bs-dismiss: Bootstrap llama preventDefault() y bloquea el cambio de hash.
  _bindFiltersCanvasNav: () => {
    const canvas = document.getElementById("kbFiltersCanvas");
    canvas?.querySelectorAll(".kb-cat-nav a[href], .kb-subcat-nav a[href]").forEach(a => {
      a.addEventListener("click", (e) => {
        e.preventDefault();
        bootstrap.Offcanvas.getInstance(canvas)?.hide();
        window.location.hash = a.getAttribute("href");
      });
    });
  },

  _buildPaginationHtml: (currentPage, totalPages, catFilter, searchQuery, subFilter = null) => {
    if (totalPages <= 1) return "";

    const buildUrl = (page) => {
      const parts = [];
      if (catFilter) parts.push(`cat=${catFilter}`);
      if (subFilter) parts.push(`sub=${encodeURIComponent(subFilter)}`);
      if (searchQuery) parts.push(`q=${encodeURIComponent(searchQuery)}`);
      parts.push(`page=${page}`);
      return `#knowledge?${parts.join("&")}`;
    };

    const items = [];

    // Anterior
    if (currentPage > 1) {
      items.push(`<li class="page-item"><a class="page-link" href="${buildUrl(currentPage - 1)}"><i class="fas fa-chevron-left"></i></a></li>`);
    } else {
      items.push(`<li class="page-item disabled"><span class="page-link"><i class="fas fa-chevron-left"></i></span></li>`);
    }

    // Números con ventana ±2 + primera/última
    const visible = new Set([1, totalPages]);
    for (let p = Math.max(1, currentPage - 2); p <= Math.min(totalPages, currentPage + 2); p++) visible.add(p);
    let prev = 0;
    for (const p of [...visible].sort((a, b) => a - b)) {
      if (prev && p - prev > 1) items.push(`<li class="page-item disabled"><span class="page-link">…</span></li>`);
      if (p === currentPage) {
        items.push(`<li class="page-item active"><span class="page-link">${p}</span></li>`);
      } else {
        items.push(`<li class="page-item"><a class="page-link" href="${buildUrl(p)}">${p}</a></li>`);
      }
      prev = p;
    }

    // Siguiente
    if (currentPage < totalPages) {
      items.push(`<li class="page-item"><a class="page-link" href="${buildUrl(currentPage + 1)}"><i class="fas fa-chevron-right"></i></a></li>`);
    } else {
      items.push(`<li class="page-item disabled"><span class="page-link"><i class="fas fa-chevron-right"></i></span></li>`);
    }

    return `<nav class="mt-4 d-flex justify-content-center">
      <ul class="pagination pagination-sm mb-0">${items.join("")}</ul>
    </nav>`;
  },

  _bindSearch: (catFilter) => {
    const input = document.getElementById("kb-search");
    const btn = document.getElementById("kb-search-btn");

    const go = () => {
      const q = input?.value?.trim();
      if (q) {
        window.location.hash = `knowledge?q=${encodeURIComponent(q)}`;
      } else {
        window.location.hash = catFilter ? `knowledge?cat=${catFilter}` : "knowledge";
      }
    };

    btn?.addEventListener("click", go);
    input?.addEventListener("keydown", (e) => { if (e.key === "Enter") go(); });
  },

  _bindAiToggle: (searchQuery, catFilter) => {
    const btn = document.getElementById("kb-ai-toggle");
    if (!btn) return;
    btn.addEventListener("click", () => {
      setAiSearchActive(!getAiSearchActive());
      const target = searchQuery
        ? `knowledge?q=${encodeURIComponent(searchQuery)}`
        : (catFilter ? `knowledge?cat=${catFilter}` : "knowledge");
      // Si el hash no cambia no hay hashchange: forzar el re-render para aplicar el modo
      if (window.location.hash === `#${target}`) window.dispatchEvent(new HashChangeEvent("hashchange"));
      else window.location.hash = target;
    });
  },

  _bindViewToggle: () => {
    const container = document.getElementById("kb-articulos-container");

    // Restore saved preference
    const saved = getSavedView();
    if (saved === "grid") {
      container?.classList.add("kb-grid-view");
      document.querySelector('[data-view="grid"]')?.classList.add("active");
      document.querySelector('[data-view="list"]')?.classList.remove("active");
    }

    document.querySelectorAll("[data-view]").forEach(btn => {
      btn.addEventListener("click", () => {
        document.querySelectorAll("[data-view]").forEach(b => b.classList.remove("active"));
        btn.classList.add("active");
        const isGrid = btn.dataset.view === "grid";
        container?.classList.toggle("kb-grid-view", isGrid);
        saveView(btn.dataset.view);
      });
    });
  },

  _bindTypeFilter: () => {
    const cards = document.querySelectorAll(".kb-article-card");
    // Estado centralizado para sincronizar desktop y mobile
    const state = { texto: true, pdf: true, video: true, imagen: true, audio: true };

    const apply = () => {
      cards.forEach(card => {
        card.style.display = state[card.dataset.tipo] !== false ? "" : "none";
      });
    };

    document.querySelectorAll(".kb-type-check").forEach(cb => {
      cb.addEventListener("change", () => {
        state[cb.value] = cb.checked;
        // Sincronizar todos los checkboxes del mismo valor (desktop + mobile)
        document.querySelectorAll(`.kb-type-check[value="${cb.value}"]`).forEach(el => {
          el.checked = cb.checked;
        });
        apply();
      });
    });
  },

  _bindInlineChat: (categoriaId = null) => {
    const input = document.getElementById("kb-inline-chat-input");
    const btn = document.getElementById("kb-inline-chat-btn");
    const msgs = document.getElementById("kb-inline-chat-msgs");
    if (!input || !btn || !msgs) return;

    const ask = async() => {
      const question = input?.value?.trim();
      if (!question) return;
      input.value = "";
      input.disabled = true;
      btn.disabled = true;

      const history = collectChatHistory(msgs);

      const userBubble = document.createElement("div");
      userBubble.className = "kb-chat-bubble user";
      userBubble.textContent = question;
      msgs.appendChild(userBubble);

      const loading = document.createElement("div");
      loading.className = "kb-chat-bubble assistant";
      loading.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1 text-primary"></i>Buscando...';
      msgs.appendChild(loading);
      msgs.scrollTop = msgs.scrollHeight;

      try {
        const { fuentes } = await askWithStream({
          question,
          bubble: loading,
          msgs,
          iconHtml: '<i class="fas fa-robot me-1 text-primary"></i>',
          history,
          categoriaId,
        });

        if (!fuentes?.length) {
          const suggestEl = document.createElement("div");
          suggestEl.className = "text-end mt-1";
          suggestEl.innerHTML = '<button class="btn btn-link btn-sm text-muted p-0"><i class="fas fa-lightbulb me-1 text-warning small"></i><small>¿Sugerir este tema?</small></button>';
          suggestEl.querySelector("button").addEventListener("click", async() => {
            suggestEl.innerHTML = '<small class="text-muted"><i class="fas fa-check me-1 text-success"></i>Gracias, recibimos tu consulta.</small>';
            try { await ApiService.post("sugerencias", { texto: question, tipo: "ai" }); } catch {}
          });
          msgs.appendChild(suggestEl);
        }
      } catch {
        loading.innerHTML = '<span class="text-danger">Error al consultar.</span>';
      } finally {
        input.disabled = false;
        btn.disabled = false;
        input.focus();
        msgs.scrollTop = msgs.scrollHeight;
      }
    };

    btn?.addEventListener("click", ask);
    input?.addEventListener("keydown", (e) => { if (e.key === "Enter") ask(); });
  },

  _bindVotes: (artId) => {
    const storageKey = "knowflow_votes";
    const voted = JSON.parse(localStorage.getItem(storageKey) || "{}");

    const buttons = document.querySelectorAll(".kb-vote-btn");

    if (voted[artId]) {
      buttons.forEach(btn => {
        btn.disabled = true;
        btn.title = "Ya votaste este artículo";
      });
      return;
    }

    buttons.forEach(btn => {
      btn.addEventListener("click", async() => {
        const tipo = btn.dataset.tipo;
        try {
          buttons.forEach(b => { b.disabled = true; });
          const resp = await ApiService.post("kb/votar", { articuloId: artId, tipo });
          if (resp?.data) {
            document.getElementById("kb-votos-pos").textContent = resp.data.positivos ?? 0;
            document.getElementById("kb-votos-neg").textContent = resp.data.negativos ?? 0;
          }
          // Registrar voto en localStorage para evitar votos múltiples
          voted[artId] = tipo;
          localStorage.setItem(storageKey, JSON.stringify(voted));
          buttons.forEach(b => { b.disabled = true; b.title = "Ya votaste este artículo"; });

          Swal.fire({
            icon: tipo === "positivo" ? "success" : "info",
            title: tipo === "positivo" ? "¡Gracias por el voto positivo!" : "Voto registrado",
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
          });
        } catch {
          buttons.forEach(b => { b.disabled = false; });
          Swal.fire("Error", "No se pudo registrar el voto", "error");
        }
      });
    });
  },

  _bindShare: () => {
    document.getElementById("kb-share-btn")?.addEventListener("click", async() => {
      const url = window.location.href;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Fallback para contextos sin permiso de clipboard
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      Swal.fire({ toast: true, position: "bottom-end", icon: "success", title: "Enlace copiado", showConfirmButton: false, timer: 2500 });
    });
  },

  _bindRelatedToggle: () => {
    const toggle = document.getElementById("kb-related-toggle");
    const body = document.getElementById("kb-related-body");
    const chevron = document.getElementById("kb-related-chevron");
    if (!toggle || !body) return;

    toggle.addEventListener("click", () => {
      const collapsed = body.classList.toggle("d-none");
      if (chevron) {
        chevron.classList.toggle("fa-chevron-up", !collapsed);
        chevron.classList.toggle("fa-chevron-down", collapsed);
      }
      const chat = document.getElementById("kb-article-chat");
      if (chat) chat.style.maxHeight = collapsed ? "420px" : "200px";
    });
  },

  // Mismo flujo que el chat de escritorio: streaming, historial, fuentes y Markdown
  _bindInlineChatMobile: (categoriaId = null) => {
    const input = document.getElementById("kb-mobile-chat-input");
    const btn = document.getElementById("kb-mobile-chat-btn");
    const msgs = document.getElementById("kb-mobile-chat-msgs");
    if (!input || !btn || !msgs) return;

    const ask = async() => {
      const question = input?.value?.trim();
      if (!question) return;
      input.value = "";
      input.disabled = true;
      btn.disabled = true;

      const history = collectChatHistory(msgs);

      const userBubble = document.createElement("div");
      userBubble.className = "kb-chat-bubble user";
      userBubble.textContent = question;
      msgs.appendChild(userBubble);

      const loading = document.createElement("div");
      loading.className = "kb-chat-bubble assistant";
      loading.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1 text-primary"></i>Buscando...';
      msgs.appendChild(loading);
      msgs.scrollTop = msgs.scrollHeight;

      try {
        await askWithStream({
          question,
          bubble: loading,
          msgs,
          iconHtml: '<i class="fas fa-robot me-1 text-primary"></i>',
          history,
          categoriaId,
        });
      } catch {
        loading.innerHTML = '<span class="text-danger">Error al consultar.</span>';
      } finally {
        input.disabled = false;
        btn.disabled = false;
        msgs.scrollTop = msgs.scrollHeight;
      }
    };

    btn?.addEventListener("click", ask);
    input?.addEventListener("keydown", (e) => { if (e.key === "Enter") ask(); });
  },

  _bindSuggestBtn: (searchQuery, btnId = "kb-suggest-btn") => {
    document.getElementById(btnId)?.addEventListener("click", async() => {
      const { value: texto, isConfirmed } = await Swal.fire({
        title: "Sugerir un tema",
        input: "textarea",
        inputLabel: "¿Qué tema buscabas o querés que documentemos?",
        inputValue: searchQuery || "",
        inputPlaceholder: "Ej: política de vacaciones, manual de ventas...",
        inputAttributes: { rows: 3 },
        showCancelButton: true,
        confirmButtonText: "Enviar sugerencia",
        cancelButtonText: "Cancelar",
        inputValidator: v => !v?.trim() ? "Ingresá el tema a sugerir" : null,
      });
      if (!isConfirmed || !texto?.trim()) return;
      try {
        await ApiService.post("sugerencias", { texto: texto.trim(), tipo: "manual" });
        Swal.fire({ icon: "success", title: "Sugerencia enviada", text: "Gracias, lo tendremos en cuenta.", timer: 2000, showConfirmButton: false });
      } catch {
        Swal.fire("Error", "No se pudo enviar la sugerencia", "error");
      }
    });
  },

  _bindArticleChat: (titulo) => {
    const input = document.getElementById("kb-article-chat-input");
    const btn = document.getElementById("kb-article-chat-btn");
    const msgs = document.getElementById("kb-article-chat");
    const relBody = document.getElementById("kb-related-body");
    const chevron = document.getElementById("kb-related-chevron");

    const ask = async() => {
      const question = input?.value?.trim();
      if (!question) return;
      input.value = "";
      input.disabled = true;
      btn.disabled = true;

      // Colapsar body de relacionados y expandir chat
      if (relBody && !relBody.classList.contains("d-none")) {
        relBody.classList.add("d-none");
        if (chevron) { chevron.classList.remove("fa-chevron-up"); chevron.classList.add("fa-chevron-down"); }
        msgs.style.maxHeight = "420px";
      }

      const history = collectChatHistory(msgs);

      const userBubble = document.createElement("div");
      userBubble.className = "kb-chat-bubble user";
      userBubble.textContent = question;
      msgs.appendChild(userBubble);

      const loading = document.createElement("div");
      loading.className = "kb-chat-bubble assistant";
      loading.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1 text-primary"></i>Buscando...';
      msgs.appendChild(loading);
      msgs.scrollTop = msgs.scrollHeight;

      try {
        const { fuentes } = await askWithStream({
          question: `${titulo}: ${question}`,
          bubble: loading,
          msgs,
          iconHtml: '<i class="fas fa-robot me-1 text-primary"></i>',
          history,
        });

        if (!fuentes?.length) {
          const suggestEl = document.createElement("div");
          suggestEl.className = "text-end mt-1";
          suggestEl.innerHTML = '<button class="btn btn-link btn-sm text-muted p-0"><i class="fas fa-lightbulb me-1 text-warning small"></i><small>¿Sugerir este tema?</small></button>';
          suggestEl.querySelector("button").addEventListener("click", async() => {
            suggestEl.innerHTML = '<small class="text-muted"><i class="fas fa-check me-1 text-success"></i>Gracias, recibimos tu consulta.</small>';
            try { await ApiService.post("sugerencias", { texto: question, tipo: "ai" }); } catch {}
          });
          msgs.appendChild(suggestEl);
        }
      } catch {
        loading.innerHTML = '<span class="text-danger">Error al consultar.</span>';
      } finally {
        input.disabled = false;
        btn.disabled = false;
        input.focus();
        msgs.scrollTop = userBubble.offsetTop - msgs.offsetTop;
      }
    };

    btn?.addEventListener("click", ask);
    input?.addEventListener("keydown", (e) => { if (e.key === "Enter") ask(); });
  },

  // ── API Connector view ───────────────────────────────────────────────────────

  _renderApiConnector: async(cat, categoriasVisibles, catFilter) => {
    const connectorUi = cat.connectorUi || {};
    const inputDefs = connectorUi.inputs || [];
    const ui = connectorUi.ui || {};

    // Pre-render sidebar HTML (evita nesting en TemplateEngine)
    const sidebarHtml = categoriasVisibles.map(c => {
      const activa = c.id === catFilter ? 'active' : '';
      return `<a href="#knowledge?cat=${c.id}" class="kb-cat-nav-item ${activa}">
        <div class="kb-cat-nav-icon kb-cat-${c.color || 'primary'}">
          <i class="fas ${c.icono || 'fa-folder'}"></i>
        </div>
        <span class="flex-grow-1 text-truncate">${c.nombre}</span>
        ${c.tipo === 'api' ? '<span class="badge bg-warning text-dark ms-1" style="font-size:0.6rem">API</span>' : ''}
      </a>`;
    }).join('');

    // Pre-render inputs del formulario
    const inputsHtml = inputDefs.map(f => {
      const validationAttrs = [
        f.validation?.pattern ? `pattern="${f.validation.pattern}"` : '',
        f.validation?.min_length ? `minlength="${f.validation.min_length}"` : '',
        f.validation?.max_length ? `maxlength="${f.validation.max_length}"` : '',
      ].filter(Boolean).join(' ');
      const requiredMark = f.required !== false ? '<span class="text-danger ms-1">*</span>' : '';
      const feedbackMsg = f.validation?.message || '';
      return `<div class="mb-3">
        <label class="form-label fw-semibold">${f.label}${requiredMark}</label>
        <input type="${f.type === 'number' ? 'number' : 'text'}"
          id="kbapi-input-${f.key}"
          class="form-control"
          placeholder="${f.placeholder || ''}"
          data-key="${f.key}"
          data-required="${f.required !== false}"
          ${validationAttrs}>
        ${feedbackMsg ? `<div class="invalid-feedback">${feedbackMsg}</div>` : ''}
      </div>`;
    }).join('');

    const data = {
      catNombre: cat.nombre,
      catDescripcion: cat.descripcion || '',
      catIcono: cat.icono || 'fa-plug',
      catColor: cat.color || 'primary',
      submitLabel: ui.submit_label || 'Consultar',
      submitIcon: ui.submit_icon || 'fa-magnifying-glass',
      sidebarHtml,
      inputsHtml,
    };

    const content = TemplateEngine.render(apiConnectorTemplate, data);
    document.getElementById("app").innerHTML = await Navbar.render() + content;

    KnowledgeController._bindApiConnectorForm(cat, inputDefs);
    TourService.resumeIfPending();
  },

  _bindApiConnectorForm: (cat, inputDefs) => {
    const submitBtn = document.getElementById('kbapi-submit');
    const formCard = document.getElementById('kbapi-form-card');
    const loadingEl = document.getElementById('kbapi-loading');
    const resultEl = document.getElementById('kbapi-result');
    const errorEl = document.getElementById('kbapi-error');
    const resetBtn = document.getElementById('kbapi-reset');

    function collectInputs() {
      const inputs = {};
      for (const f of inputDefs) {
        const el = document.getElementById(`kbapi-input-${f.key}`);
        if (el) inputs[f.key] = el.value.trim();
      }
      return inputs;
    }

    function validateForm(inputs) {
      for (const f of inputDefs) {
        const val = inputs[f.key] || '';
        const el = document.getElementById(`kbapi-input-${f.key}`);
        if (!el) continue;
        el.classList.remove('is-invalid');

        if (f.required !== false && !val) {
          el.classList.add('is-invalid');
          el.focus();
          return false;
        }
        // minlength/maxlength del input no se validan solos (no hay <form> ni checkValidity)
        const largoInvalido = val && (
          (f.validation?.min_length && val.length < f.validation.min_length) ||
          (f.validation?.max_length && val.length > f.validation.max_length)
        );
        if (largoInvalido || (f.validation?.pattern && val && !new RegExp(f.validation.pattern).test(val))) {
          el.classList.add('is-invalid');
          el.focus();
          return false;
        }
      }
      return true;
    }

    function showLoading() {
      formCard?.classList.add('d-none');
      loadingEl?.classList.remove('d-none');
      resultEl?.classList.add('d-none');
      errorEl?.classList.add('d-none');
    }

    function showResult(data) {
      loadingEl?.classList.add('d-none');
      resultEl?.classList.remove('d-none');

      const titleEl = document.getElementById('kbapi-result-title');
      if (titleEl) titleEl.textContent = data.titulo || cat.nombre;

      const narrationEl = document.getElementById('kbapi-narration');
      if (narrationEl) {
        narrationEl.innerHTML = data.notFound
          ? `<p class="text-muted fst-italic">${data.narration}</p>`
          : renderMarkdown(data.narration || '');
      }

      // Tabla de datos crudos
      const fieldsEl = document.getElementById('kbapi-fields-table');
      if (fieldsEl && data.datos && Object.keys(data.datos).length > 0) {
        const rows = Object.entries(data.datos)
          .map(([k, v]) => {
            let display;
            if (Array.isArray(v)) {
              display = v.map(item => typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item)).join('<br>');
            } else if (typeof v === 'object' && v !== null) {
              display = JSON.stringify(v);
            } else {
              display = String(v ?? '');
            }
            return `<tr><td class="fw-semibold text-muted small pe-3 text-nowrap">${k}</td><td class="small">${display}</td></tr>`;
          })
          .join('');
        fieldsEl.innerHTML = `<div class="table-responsive mt-3 border-top pt-3">
          <table class="table table-sm table-borderless mb-0">${rows}</table>
        </div>`;
      } else if (fieldsEl) {
        fieldsEl.innerHTML = '';
      }

      // Galería de imágenes
      const imgsEl = document.getElementById('kbapi-images');
      if (imgsEl && data.imagenes?.length) {
        const imgs = data.imagenes
          .map(u => `<a href="${u}" target="_blank" rel="noopener"><img src="${u}" class="kb-api-img rounded" alt=""></a>`)
          .join('');
        imgsEl.innerHTML = `<div class="kb-api-gallery mt-3 border-top pt-3">${imgs}</div>`;
      } else if (imgsEl) {
        imgsEl.innerHTML = '';
      }
    }

    function showError(msg) {
      loadingEl?.classList.add('d-none');
      formCard?.classList.remove('d-none');
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.classList.remove('d-none');
      }
    }

    function resetView() {
      resultEl?.classList.add('d-none');
      errorEl?.classList.add('d-none');
      formCard?.classList.remove('d-none');
      inputDefs.forEach(f => {
        const el = document.getElementById(`kbapi-input-${f.key}`);
        if (el) { el.value = ''; el.classList.remove('is-invalid'); }
      });
      inputDefs[0] && document.getElementById(`kbapi-input-${inputDefs[0].key}`)?.focus();
    }

    async function submit() {
      const inputs = collectInputs();
      if (!validateForm(inputs)) return;

      showLoading();
      try {
        const result = await ApiService.post('kb/api-query', { categoriaId: cat.id, inputs });
        showResult(result);
      } catch (err) {
        showError(err.message || 'No se pudo procesar la consulta. Intentá nuevamente.');
      }
    }

    submitBtn?.addEventListener('click', submit);
    resetBtn?.addEventListener('click', resetView);

    // Submit con Enter en cualquier input
    document.querySelectorAll('[id^="kbapi-input-"]').forEach(el => {
      el.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
    });

    // Focus en el primer campo
    inputDefs[0] && document.getElementById(`kbapi-input-${inputDefs[0].key}`)?.focus();
  },
};
